// src/components/desk/MatchMatrix.jsx — the v2 matching panel, PROPERTY side (B568).
//
// Designed in `design/Questions before building.zip` (02-spec.md / 05-v2-answers.md) and
// answered against measured data in `design/MATCHING_UI_V2_ANSWERS_2026-08-08.md`. This is
// the SPINE of that design: the conditions × clients matrix and the expandable detail card,
// both reading live scorer output. Stance capture, the hoisted-strip overflow row and the
// two empty states are deliberately NOT here yet — they are follow-on passes, and shipping
// them half-built would put copy on screen that promises more than the panel does.
//
// ── Where the data comes from, and why it is ONE layer today ─────────────────────────────
// The design specifies a two-layer render: the matrix structure paints instantly from
// `match_edge`, and the interior detail (marks, bands, the summary sentence) fills ~1 s
// later from GET /api/core/property/:id/matching-clients.
//
// ⚠️ `match_edge` is NOT readable yet — that is the Phase 2 read-flip, and it is gated on `mm_gate_status()`
// reporting GREEN. So today there is exactly ONE source and therefore ONE layer: the live
// endpoint. `detailReady` below is the seam where the second layer attaches; when the read-flip
// lands it becomes "the edge rows arrived but the scored detail has not", and `LoadingCell`
// starts rendering. It is written now so the cell vocabulary is complete and the flip is a
// boolean, not a redesign — but it does not fire today, and nothing on screen claims it does.
//
// ── The check contract this file reads (core/match-score.mjs → recommendation.mjs) ───────
//   opportunity.evidence[] (aliased to `.checks` by db/core.js withMatchAlias) =
//     { key, status: 'pass'|'partial'|'fail'|'unknown', weight, detail, value?, hard?, delta? }
//   `detail` is ENGLISH engineer-facing text. It is a tooltip here and NEVER visible copy —
//   same rule MatchExplain already follows (2026-07-13 UI audit: no engineer tokens on an
//   agent's screen). `delta` carries the localized sentence via deltaLabel(), which is why
//   the mismatch band shows "over budget $37" rather than a bare key.

import { useEffect, useMemo, useState } from 'react'
import { t, tLog, tOr } from '../../lib/i18n.js'
import { Icon, IconText, ROLE_ICON } from '../Icon.jsx'
import { card, pill } from './primitives.jsx'
import { getPropertyMatchingClients, setMatchVerdict } from '../../db/core.js'
import { whoAnswers, ruleIdOf, clientFillFor } from '../../lib/matchGaps.js'
import {
  RowActions,
  ClassBadge, RegisteredAgo, VerdictActions, CompletenessMeter,
} from './MatchSuggestions.jsx'
// The band card itself — the cell vocabulary, the value formatters, the row/band components
// and the labels — MOVED to matchBands.jsx (Q1, client surface): the client card in
// MatchSuggestions renders the SAME bands, and it cannot import them from here without
// closing a module cycle (this file already imports from MatchSuggestions).
//
// ⚠️ AND SO DID THE CAPTURE CONTROLS (round-2 §3). The client card grew a fourth column that
// must be filled on EVERY row, so OwnerFill/ClientFill, the answer context and the write
// lock — plus `ownerAnswerKind` and the vocabularies they read, which used to sit in
// MatchSuggestions — all live in the leaf now. This file renders identically; it just no
// longer owns the definitions. Every db/core write door went with them, which is why the
// import above shrank to the two calls this panel still makes for itself.
import {
  cellOf, bandsOf, Band, BandRow, GapRow, QuietRow, summarySentence,
  conditionLabel, ownerQuestion, provenanceOf, sourceOf,
  ownerAnswerKind, isDeclined, declineSubjectOf, knownFromDemand,
  OwnerFill, ClientFill, useAnswerDoor,
} from './matchBands.jsx'
import { MeetingModal, seedFromMatchRow } from './MeetingModal.jsx'
import { ShareModal } from './ShareModal.jsx'
import { NegotiationModal } from './NegotiationModal.jsx'

// The endpoint caps `limit` at 50 server-side (core_match.js). The design's "one call
// returns every client for the property" is true up to that cap and no further — so the
// panel asks for the cap rather than the db-layer default of 8, and says so when the cap
// actually bit (see `capped` below). Scoring cost does not move with this number: the
// server scores every classified demand regardless and `limit` only slices what it
// serializes.
const ROW_LIMIT = 50

// ── Group order = the queue order, not the classifier's precedence ───────────────────────
// READY first because it is the only group that needs no call before an offer. REJECT never
// appears: it is diagnostics, counted in `considered`, never a row.
// RULE 5 (redline): a group header carries dot, name, count AND one clause saying what the
// group IS — "waiting on the owner's answer" — because a coloured dot and a number tell an
// agent how many, never what to do about them. A group at zero keeps both: READY is
// genuinely 0 on most properties, and omitting it reads as "no such thing" rather than
// "nothing here yet, and here is what would fill it".
const GROUPS = [
  { key: 'READY',          labelKey: 'mv2_group_ready',            meansKey: 'mv2_grp_ready_means',     tone: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  { key: 'VERIFY',         labelKey: 'desk_match_class_verify',    meansKey: 'mv2_grp_verify_means',    tone: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500' },
  { key: 'NEGOTIATE',      labelKey: 'desk_match_class_negotiate', meansKey: 'mv2_grp_negotiate_means', tone: 'text-amber-600 dark:text-amber-400',    dot: 'bg-amber-500' },
  { key: 'DISCUSS_DEMAND', labelKey: 'desk_match_class_discuss',   meansKey: 'mv2_grp_discuss_means',   tone: 'text-violet-600 dark:text-violet-400',  dot: 'bg-violet-500' },
]

// CELL / cellOf (the cell vocabulary, 02-spec.md "States") — moved to ./matchBands.jsx
// (Q1 client-surface extraction). Same definitions, imported above.

// The interval between "the structure is here" and "the detail is here" (05-v2-answers Q3).
// A SHAPE, not a glyph — it cannot be misread as – or ? because it is not a character.
// Unreachable until the read-flip lands; see the header note.
function LoadingCell() {
  return <span className="inline-block w-[22px] h-[8px] rounded-[2px] bg-muted animate-pulse align-middle"
    title={t('mv2_details_loading')} />
}

// ── Column selection ─────────────────────────────────────────────────────────────────────
// A property can carry 21 distinct blockers across its clients (measured on 04fba8fe), and
// a 21-column matrix is a spreadsheet, not a panel. Columns are therefore the conditions
// that actually SEPARATE these clients: ranked by how many client rows are non-pass on them,
// capped. Everything else still exists — it is in the expanded card, which shows every check
// for that client without a cap. The remainder is counted in words rather than dropped
// silently, because a truncated matrix that says nothing reads as a complete one.
// RULE 4 (redline): at most SIX condition columns, ordered by clients affected, and
// everything past six collapses into ONE `სხვა` column showing a count — not a stub. Eight
// headers clipped to two characters produced `მდებარ...` and `მდგომა...` side by side: two
// different conditions, indistinguishable at the truncated width.
const MAX_COLUMNS = 6
const OVERFLOW_COL = '__other__'
function chooseColumns(rows) {
  const score = new Map()
  for (const m of rows) for (const c of m.checks || []) {
    if (c.status === 'pass') continue
    score.set(c.key, (score.get(c.key) || 0) + 1)
  }
  const ranked = [...score.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const cols = ranked.slice(0, MAX_COLUMNS).map(([k]) => k)
  const hiddenKeys = ranked.slice(MAX_COLUMNS).map(([k]) => k)
  if (hiddenKeys.length) cols.push(OVERFLOW_COL)
  return { cols, hidden: hiddenKeys.length, hiddenKeys: new Set(hiddenKeys) }
}
// RULE 4's label half. The five core scorer checks get a hand-picked SHORT WORD rather than
// a truncation, because the truncation is what collided: `რემონტი` for a flat's condition
// is the word an agent actually says AND it no longer starts with the same two letters as
// the district column. A restriction subject falls back to abbreviating its vocabulary noun.
const CORE_COL_LABEL = {
  location: 'mv2_col_district', district: 'mv2_col_district',
  price: 'mv2_col_price',
  bedrooms: 'mv2_col_bedrooms', rooms: 'mv2_col_bedrooms',
  occupants: 'mv2_col_occupants',
  condition: 'mv2_col_condition',
}
function columnLabel(key, ans) {
  if (key === OVERFLOW_COL) return t('mv2_col_other')
  const core = CORE_COL_LABEL[key]
  if (core) return t(core)
  // Second tier, same idea one level down: a REVIEWED short form for a condition the map
  // above does not name. `გაფორმებული პარკინგი` abbreviates to `გაფორმებ.`, which drops the
  // one word that identifies it — so three of them were translated for this slot
  // (2026-08-08 review). Optional by construction: absent, abbrev() still applies, so a new
  // column never requires a new string first.
  const bare = /^(?:policy|must|mustnot):(.+)$/.exec(key)?.[1] || key
  return tOr(`match_key_${bare}_col`, null) || abbrev(conditionLabel(key, ans))
}

// bandsOf / ROW_GRID / the value formatters (fmtRange/fmtValue/askedOf/actualOf/sourceOf)
// and the band rows (BandRow/QuietRow/GapRow/AnsweredRow/Band/summarySentence) — moved to
// ./matchBands.jsx (Q1 client-surface extraction). Same definitions, imported above.

// ── The "what do we do" cell ──────────────────────────────────────────────────────────────
// ⚠️ NEVER render `primary_action.action.label`. The classifier builds it as ENGLISH PROSE
// ("Confirm pol_americans before proceeding", "Ask client: consider this area?") and it
// shipped straight onto an all-Georgian screen for a day — leaking a raw `pol_` code with
// it. The label is the ONLY English part of that object: `kind`, `target` and `delta_ref`
// are structured, which is everything needed to say the same thing in the agent's language.
//
// For an owner verification the best sentence is not a generic "confirm X" at all — it is
// the OWNER'S OWN QUESTION from the vocabulary, the same string the strip above renders, so
// the cell and the strip agree word for word about what the call is.
//
// Falls back to EMPTY, never to the English label. A blank cell costs the agent one glance;
// a sentence in the wrong language telling them to confirm `pol_americans` costs their trust
// in every other number on the panel.
function actionText(m, ans, hoistedKeys, blockingOwnerCount) {
  const a = m.primary_action?.action
  const subject = a?.delta_ref ? conditionLabel(a.delta_ref, ans) : null
  const name = m.person?.name && m.person.name !== 'Unknown contact' ? m.person.name : null
  // RULE 7: the ask-once rule applies to THIS COLUMN TOO. If the next action is a question
  // already sitting in the strip above, repeating it here is the panel asking twice on one
  // screen — so the row reports what it is WAITING ON instead, with a count.
  if (a && a.kind === 'verify' && a.target === 'owner' && hoistedKeys?.has(a.delta_ref)) {
    return tLog('mv2_act_waiting_owner', { n: blockingOwnerCount || 1 })
  }
  if (!a) return ''
  // ⚠️ Every template below names a SUBJECT, and interpolating an absent one produces
  // broken Georgian on screen — "ჰკითხე saanvi-ს -ზე", a dangling case ending with
  // nothing in front of it. `delta_ref` is genuinely null for some actions, so the
  // subjectless forms are the DEFAULT and the subject templates are the special case, not
  // the other way round. Caught by rendering; a build cannot see a missing interpolation.
  if (!subject) {
    switch (a.kind) {
      case 'present': return t('desk_match_tier_ready_action')
      case 'verify':  return a.target === 'owner'
        ? tLog('mv2_act_waiting_owner', { n: blockingOwnerCount || 1 })
        : t('mv2_client_answers')
      default:        return blockingOwnerCount
        ? tLog('mv2_act_waiting_owner', { n: blockingOwnerCount })
        : t('mv2_client_answers')
    }
  }
  switch (a.kind) {
    case 'present':    return t('desk_match_tier_ready_action')
    case 'verify':     return a.target === 'owner'
      ? ownerQuestion(a.delta_ref, ans)
      : tLog('mv2_act_ask_client', { name: name || t('mv2_role_client'), subject: subject || '' })
    case 'ask_client': return blockingOwnerCount
      ? tLog('mv2_act_then_concede', { n: blockingOwnerCount, subject: subject || '' })
      : tLog('mv2_act_call_concede', { subject: subject || '' })
    case 'negotiate':  return blockingOwnerCount
      ? tLog('mv2_act_then_concede', { n: blockingOwnerCount, subject: subject || '' })
      : tLog('mv2_act_call_concede', { subject: subject || '' })
    default:           return subject || ''
  }
}

// RULE 6 (redline): NEVER render the generic noun „კლიენტი“ as a name. Four rows reading
// the same word are indistinguishable, unsortable and — the point — uncallable. Fall back to
// the MASKED PHONE in monospace: unique, recognisable, and the thing the agent will actually
// dial. If neither a name nor a phone exists the row does not belong in a call queue at all,
// which is why this can return null and the caller drops the row.
function maskPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (d.length < 7) return null
  const tail = d.slice(-2)
  const head = d.length > 9 ? `+${d.slice(0, d.length - 9)} ${d.slice(-9, -8)}` : d.slice(0, 1)
  return `${head}·· ··· ·${tail}`
}
// knownFromDemand — moved to ./matchBands.jsx (round-2 §3): the client card asks the same
// question of the same digest, and the answer must not be written twice.

function clientLabel(m) {
  const name = m.person?.name && m.person.name !== 'Unknown contact' ? m.person.name : null
  if (name) return { text: name, mono: false }
  if (m.title) return { text: m.title, mono: false }
  const masked = maskPhone(m.person?.phone)
  if (masked) return { text: masked, mono: true }
  // Represented by another agency: their client's name is theirs to hold, but the row is
  // still workable through the representative, so it keeps that name rather than a generic
  // noun.
  //
  // ⚠️ IT MUST SAY WHOSE NAME THIS IS. Until 2026-08-08 this returned the agent's name BARE,
  // in a column headed `კლიენტი` — so the panel stated that Luka Bigvava, one of our own
  // agents, was a client. Reported by the owner, who could see it was wrong; a reader who
  // could not would have phoned an agent expecting a buyer. Adding a client ICON beside it
  // an hour earlier made the false claim louder, which is how it got noticed.
  //
  // `desk_match_via_agent` already exists and already says the true thing — "{name}-ის
  // კლიენტი", *{name}'s client* — and the v1 panel has rendered it that way all along
  // (MatchSuggestions ~2016). So this is not a new string or a new rule: it is the v2 grid
  // catching up to a sentence the codebase had already got right.
  const via = m.ownership?.represented_by?.agent_name
  if (via) return { text: tLog('desk_match_via_agent', { name: via }), mono: false, via: true }
  return null
}

// ── The hoisted strip's v2 rule (05-v2-answers Q1) ───────────────────────────────────────
// Three differences from the v1 panel's rule, all of them measured:
//   • FILTERED to owner-answerable subjects. A property carries `location`, `price` and
//     `bedrooms` in the same blocker list, and an owner strip containing them asks the owner
//     to fix the client's district. Membership is decided by ownerAnswerKind(), the same
//     predicate the strip's own menus branch on.
//   • leverage ≥ 3 and capped at 5 rows. 21 distinct blockers were measured on one property;
//     a 21-row strip is a junk drawer, and 3 is where "one call clears several clients"
//     stops being true.
//   • ordered by CLIENTS AFFECTED ON THIS PROPERTY, never by stances recorded. `pol_animals`
//     has 659 recorded stances and exactly one live client carrying it — ordering by the
//     first number puts the least useful question at the top of the panel.
// conditionLabel / cardCheckLabel / CheckLabel / ownerQuestion — moved to ./matchBands.jsx
// (Q1 client-surface extraction). Same definitions, imported above.

// A column header has ~54px. A Georgian condition name does not fit, and the two answers
// tried before this one were both wrong: two characters ("მა მე ფა დე") is not a word, and
// vertical text (my own invention) reads badly, eats 7rem of height above the grid and
// throws away the leverage number. The mock's answer is abbreviation WITH the number
// (`მცხოვრ. 17`), so the header carries the one fact that decides which call to make first.
// Cuts on a word boundary where there is one — Georgian has no casing to lean on.
function abbrev(label, max = 8) {
  const s = String(label || '')
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const sp = cut.lastIndexOf(' ')
  return (sp > 3 ? cut.slice(0, sp) : cut) + '.'
}
// (columnLabel lives beside CORE_COL_LABEL above — the reviewed `_col` strings are its
// second tier. This is where a second copy of it landed during the 2026-08-08 merge; the
// duplicate is gone, and the note stays so the next merge does not re-create it.)

// The answer context (useListingAnswers) — moved to ./matchBands.jsx (round-2 §3) and now
// reached through `useAnswerDoor`, which bundles it with the write lock and the re-read key
// that always travelled with it. The client card opens the same door per EXPANDED card.

// RoleTag — moved to ./matchBands.jsx (Q1 client-surface extraction).

// declineSubjectOf / isDeclined — moved to ./matchBands.jsx (round-2 §3) with OwnerFill,
// which is the only thing that writes a decline.

// AnswerPills / NumPills / InlineNumber / InlineDate and the two capture controls
// (OwnerFill, ClientFill) — moved to ./matchBands.jsx (round-2 §3). The client card's fourth
// column, `ვისი ჩასაწერია`, opens the same four doors, and this file may not be imported
// from MatchSuggestions (it already imports the other way). Same definitions, imported above;
// WHO may press them is still each surface's own policy, carried by `canRecord`.

// ── The owner strip, as ROWS ──────────────────────────────────────────────────────────────
// The v1 panel renders these as chips, and chips were the wrong shape here for three reasons
// the mock makes obvious: a chip has no room for the QUESTION (only the subject noun), no
// room for the leverage number, and nowhere to put the answer controls — so answering meant
// a popover, and an answered subject simply vanished rather than showing what was recorded.
//
// A row carries all four: the question in the owner's own words, what answering it unblocks,
// the stance controls inline, and — once answered — the recorded value with its provenance,
// which is the visible half of "answers are written once, and then we stop asking".
//
// ⚠️ The question text comes from the VOCABULARY (0495), never from a JS map and never
// assembled from the noun: Georgian takes two different constructions here (person groups
// "…ზე აქირავებს?" vs pets/household "…ს უშვებს?") and a single template is wrong for about
// half the list. A subject with no reviewed question falls back to its noun, which is at
// least grammatical — an invented question is neither.
function QuestionStrip({ hoisted, overflow, overflowRows, answered, ans, canRecord,
  listingId, propertyId, agentId, onAnswered, onError }) {
  const [busy, setBusy] = useState(null)     // the key currently being written
  // 05-v2-answers Q1: the overflow expands IN PLACE, inside the strip, pushing the table
  // down — not a popover and not another screen. And the collapsed row NAMES the subjects,
  // which is what stops "კიდევ 17 კითხვა" reading as a junk drawer.
  const [showAll, setShowAll] = useState(false)
  const rows = [...hoisted.entries()].sort((a, b) => b[1].n - a[1].n)
  const extra = [...(overflowRows || new Map()).entries()].sort((a, b) => b[1].n - a[1].n)
  // Distinct clients, NOT the sum of the rows' counts — one client blocked by three
  // questions must be counted once or the header overstates the call's value.
  const total = (() => {
    const seen = new Set()
    for (const [, h] of rows) for (const id of (h.clients || [])) seen.add(id)
    return seen.size || rows.reduce((a, [, h]) => a + h.n, 0)
  })()
  // ⚠️ The strip's own `answerBool` / `answerNum` / `NumAnswer` were removed here on
  // 2026-08-08. They opened the same three doors as `OwnerFill` — stance, feature presence,
  // term — and the two arrived independently, which is precisely the drift B273 names: a
  // second hand-written {pets: 'pets_allowed'} map, one component away from the first. The
  // band needs those doors too, so the shared component is the one that survives, and it
  // kept the better half of both: `ownerAnswerKind` decides the door (from the strip) and
  // კი/არა label the boolean rather than a bare ✓/✗ (from here).
  const run = async (key, fn) => {
    if (busy) return
    setBusy(key)
    try { await fn(); await onAnswered?.() }
    catch (e) { onError?.(e.message) }
    finally { setBusy(null) }
  }
  const Row = ([k, h]) => (
    <div key={k} title={h.detail}
      className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 items-center py-[3px]">
      <span className="text-xs text-foreground truncate">{ownerQuestion(k, ans)}</span>
      <span className="text-xs text-amber-600 dark:text-amber-400 tabular-nums whitespace-nowrap">
        {tLog('mv2_unlocks', { n: h.n })}
      </span>
      <span className="flex items-center gap-1">
        {/* ⚠️ Until 2026-08-08 this rendered stance buttons for `policy:` subjects and the
            words "the owner answers" for everything else — so `რამდენ ადამიანს უშვებს?`,
            the mock's own example, was a question with no way to answer it. OwnerFill opens
            all three doors, so a numeric subject gets the input the design specifies. */}
        <OwnerFill checkKey={k} busy={busy} canRecord={canRecord} listingId={listingId}
          propertyId={propertyId} agentId={agentId} onRun={run} ans={ans} />
        {!ownerAnswerKind(k) || !canRecord
          ? <span className="text-xs text-muted-foreground/60">{t('mv2_owner_answers')}</span> : null}
      </span>
    </div>
  )
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-2.5 mb-2">
      {/* RULE 3 (redline): ONE UNIT, everywhere — CLIENTS. The header said "unblocks 13
          MATCHES" while its own rows said "unblocks 4", so the two numbers could not be
          reconciled and the agent could not tell which was which. An agent phones people,
          not edges. The total is DISTINCT clients across the rows, so it equals what the
          rows below actually sum to. */}
      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">
        <Icon name="phone" /> {tLog('mv2_unlocks_clients', { n: total })}
      </p>
      <div className="space-y-0.5">
        {rows.map(Row)}
        {showAll && extra.map(Row)}
      </div>

      {/* Already answered, and therefore no longer asked. The strip is where an agent looks
          before dialling, so this is where "you already know this" has to appear — a
          recorded stance that simply vanishes leaves them re-asking the owner a question the
          office answered last week. */}
      {!!answered?.length && (
        <div className="mt-1.5 pt-1.5 border-t border-amber-500/20 space-y-0.5">
          {answered.map(a => (
            <div key={a.subject} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-baseline py-[2px]">
              <span className="text-xs text-emerald-600 dark:text-emerald-400 truncate">
                ✓ {a.label} — <span className="font-medium">{a.value}</span>
              </span>
              <span className="text-xs text-muted-foreground/70 whitespace-nowrap">
                {a.source} · {t('mv2_no_longer_asked')}
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground/70 mt-1.5">{t('mv2_owner_fields_only')}</p>
      {!!overflow && (
        <button type="button" onClick={() => setShowAll(s => !s)}
          className="w-full text-left mt-0.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-baseline hover:text-foreground">
          <span className="text-xs text-muted-foreground/70 truncate">
            {showAll ? '⌃' : '⌄'} {tLog('mv2_more_questions', { n: overflow })}
            {/* Naming them is the point — see Q1. Truncates rather than wraps, per the
                layout hygiene note; the full list is one tap away in the same place. */}
            {!showAll && extra.length ? <> · {extra.map(([k]) => ownerQuestion(k, ans)).join(' · ')}</> : null}
            {' '}— {t('mv2_each_unlocks')}
          </span>
          <span className="text-xs text-muted-foreground/70 whitespace-nowrap">{t('mv2_expand')}</span>
        </button>
      )}
      <p className="text-xs text-muted-foreground/70">{t('mv2_recount_promise')}</p>
    </div>
  )
}

const HOIST_MIN_LEVERAGE = 3
const HOIST_MAX_ROWS = 5
// `ans` supplies the B586 decline set. This is THE consumer of that table and the only one:
// a declined subject stops being offered as the next call, which is the entire behavioural
// point — the scorer still reports it unknown, because it is. Passing `ans` is optional so
// an older caller (or a failed fetch) keeps every subject askable rather than hiding rows.
function hoistFrom(rows, ans = null) {
  const m = new Map()
  for (const r of rows) for (const c of r.checks || []) {
    if (c.status !== 'unknown') continue
    // Asked already, and refused. Not a question to put in front of the agent again.
    if (isDeclined(c.key, ans)) continue
    // The client's gap, not the owner's. Reads the scorer's declared audience  see cellOf.
    if (whoAnswers(c) === 'client') continue
    // RULE 1's corollary: a row you cannot answer does not belong in the strip. Filtering
    // here rather than rendering a control-less row keeps the band to one affordance.
    if (!ownerAnswerKind(c.key)) continue
    const h = m.get(c.key) || { n: 0, detail: c.detail, clients: new Set() }
    h.n += 1
    if (r.requirement_id) h.clients.add(r.requirement_id)
    m.set(c.key, h)
  }
  const ranked = [...m.entries()].filter(([, h]) => h.n >= HOIST_MIN_LEVERAGE)
    .sort((a, b) => b[1].n - a[1].n)
  return {
    hoisted: new Map(ranked.slice(0, HOIST_MAX_ROWS)),
    overflow: Math.max(0, ranked.length - HOIST_MAX_ROWS),
    // The overflow's own rows, so the expanded state and the collapsed summary name the
    // same subjects — a count and a list derived separately is how they come to disagree.
    overflowRows: new Map(ranked.slice(HOIST_MAX_ROWS)),
  }
}

// Owner questions this listing has ALREADY answered — the recorded stances, in the strip's
// own vocabulary. Read from `listing_policy` rather than from the checks, because a stance
// that has been recorded no longer produces an `unknown` check at all: by the time it is
// worth showing, the only trace of it is the row itself.
function answeredFrom(ans) {
  const out = []
  for (const [subject, p] of (ans?.policyBySubject || new Map()).entries()) {
    if (!p?.stance) continue
    out.push({
      subject,
      // ⚠️ NEVER the bare `subject`. A vocabulary miss printed `pol_students` into the
      // strip; conditionLabel's own fallback wraps the code in Georgian instead, which is
      // the same degradation every other label on this panel takes.
      label: ans?.vocab?.get(subject)?.name || conditionLabel(`policy:${subject}`, ans),
      value: t(`desk_stance_${p.stance}`),
      source: provenanceOf(p),
    })
  }
  return out.sort((a, b) => a.label.localeCompare(b.label)).slice(0, 4)
}

export function MatchMatrix({ propertyId, listingId = null, agentId = null, canRecord = false,
  address = null, onOpenDemand, refreshKey = 0, completeness = null }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(null)   // requirement_id of the expanded row
  const [meetingSeed, setMeetingSeed] = useState(null)
  const [shareSeed, setShareSeed] = useState(null)
  const [negoSeed, setNegoSeed] = useState(null)
  // Workflow verdicts (0102): optimistic local flip layered over the server's m.verdict,
  // background POST, snap back on error — the same contract the v1 panel uses, keyed by
  // requirement_id because on this surface the rows ARE requirements.
  const [verdicts, setVerdicts] = useState({})
  const [verdictBusy, setVerdictBusy] = useState(false)
  // Session-cached vocabulary plus this listing's own recorded answers. `getFeatures` caches
  // its promise, so the vocabulary costs one fetch for the whole session; the rules/stances/
  // terms are per listing and refetch when a recorded answer changes.
  // EVERY feature, not just `pol_*`: an amenity check (`must:elevator`) needs a Georgian name
  // exactly as much as a policy one does, and filtering to pol_ here is what put `elevator`
  // in a column header.
  // ⚠️ The four pieces this used to hold apart — the answer context, the `answerKey` that
  // re-reads it, the one-write-at-a-time lock and `runFill` — are ONE HOOK now
  // (`useAnswerDoor`, matchBands.jsx), because the client card needs all four and a second
  // copy of them is the drift B273 names. Behaviour is unchanged: `onAnswered` is this
  // panel's own re-score, the hook bumps `answerKey` itself, and `refreshKey` still re-reads
  // the listing's recorded rows alongside the match payload.
  async function reload() {
    const d = await getPropertyMatchingClients(propertyId, { limit: ROW_LIMIT })
    setData(d)
  }
  const { ans, runFill, fillBusy, fillErr } = useAnswerDoor({
    listingId, propertyId, agentId, onAnswered: reload, refreshKey,
  })
  useEffect(() => {
    if (!propertyId) return
    let gone = false
    setData(null); setErr(null); setOpen(null); setVerdicts({})
    getPropertyMatchingClients(propertyId, { limit: ROW_LIMIT })
      .then(d => { if (!gone) setData(d) })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, refreshKey])

  const effVerdict = m => {
    const local = verdicts[m.requirement_id]
    return local !== undefined ? local : (m.verdict?.state ?? null)
  }
  async function applyVerdict(m, state) {
    let reason = null
    if (state === 'rejected') {
      reason = window.prompt(t('desk_match_reject_reason_prompt')) // eslint-disable-line no-alert
      if (reason === null) return
    }
    const prev = effVerdict(m)
    setVerdicts(v => ({ ...v, [m.requirement_id]: state === 'clear' ? null : state }))
    setVerdictBusy(true)
    try { await setMatchVerdict({ requirementId: m.requirement_id, propertyId, state, reason }) }
    catch { setVerdicts(v => ({ ...v, [m.requirement_id]: prev })) }
    finally { setVerdictBusy(false) }
  }

  const rows = data?.matches || []
  // The seam for the read-flip's second layer. Today the rows and their checks arrive in the same
  // response, so this is true whenever there is anything to show at all.
  const detailReady = !!data

  const { cols, hidden, hiddenKeys } = useMemo(() => chooseColumns(rows), [rows])
  const { hoisted, overflow, overflowRows } = useMemo(() => hoistFrom(rows, ans), [rows, ans])
  const answered = useMemo(() => answeredFrom(ans), [ans])
  // How many client rows each COLUMN is non-pass on — the same leverage idea as the strip,
  // and what the mock prints beside the abbreviated header so the densest column is visible
  // without reading the grid.
  const colLeverage = useMemo(() => {
    const m = new Map()
    for (const r of rows) for (const c of r.checks || []) {
      if (c.status === 'pass') continue
      m.set(c.key, (m.get(c.key) || 0) + 1)
    }
    return m
  }, [rows])
  const byGroup = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      const g = r.classification || 'VERIFY'
      if (!m.has(g)) m.set(g, [])
      m.get(g).push(r)
    }
    return m
  }, [rows])

  if (!propertyId) return null

  // `eligible_count` is the true number of non-REJECT demands; `rows` is what the cap let
  // through. When they differ the header would otherwise overstate what is on screen.
  const eligible = data?.eligible_count ?? 0
  const capped = eligible > rows.length

  return (
    <div className={card}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-muted-foreground min-w-0 truncate">
          <Icon name="target" className="mr-1" />
          {t('mv2_title')}
          {data && <span className="font-normal"> · {tLog('mv2_subtitle', { n: data.considered ?? 0, m: eligible })}</span>}
        </p>
        {/* B41, carried over from the card list: the ANCHOR property's own completeness,
            right where its matches are judged. A short list under a half-empty listing
            means "go fill", not "no market" — dropping it in the redesign would have
            quietly removed the one control that tells those two apart. */}
        {completeness && <CompletenessMeter {...completeness} />}
      </div>

      {/* A failed WRITE surfaces on the same line a failed load does — it used to be set
          into `err` by the panel's own runFill, and now arrives as the hook's `fillErr`.
          One line, because from the agent's side both are "the panel could not do it". */}
      {(err || fillErr) && <p className="text-xs text-destructive">{tLog('desk_match_lookup_failed', { err: err || fillErr })}</p>}
      {!data && !err && <p className="text-xs text-muted-foreground"><IconText>{t('desk_match_scoring')}</IconText></p>}
      {data?.note && <p className="text-xs text-muted-foreground">{data.note}</p>}

      {data && !data.note && !rows.length && (
        <p className="text-xs text-muted-foreground">{t('desk_match_none_eligible')}</p>
      )}

      {/* Step 1 — the one call that moves the most rows. Same strip the v1 panel renders,
          filtered and ranked by the v2 rule above, and since 2026-08-08 with the overflow
          the design specifies: expandable in place, naming the remaining subjects. */}
      {!!hoisted.size && (<>
        <p className="text-xs font-semibold text-foreground mb-1">
          <span className="text-muted-foreground mr-1">1</span>{t('mv2_step1')}
        </p>
        <QuestionStrip hoisted={hoisted} overflow={overflow} overflowRows={overflowRows}
          answered={answered} ans={ans} canRecord={canRecord}
          listingId={listingId} propertyId={propertyId} agentId={agentId}
          onAnswered={reload} onError={setErr} />
        <p className="text-xs font-semibold text-foreground mb-1">
          <span className="text-muted-foreground mr-1">2</span>{t('mv2_step2')}
        </p>
      </>)}

      {!!rows.length && (
        // Wide content scrolls INSIDE its own container — the panel body never scrolls
        // sideways (02-spec layout hygiene; the 2026-07-20 sticky-footer bug).
        //
        // ⚠️ THE SCROLLER IS DESK-ONLY, and so is the width it scrolls (round-2 §1).
        // Below 640px there is no grid to scroll — the conditions × clients matrix does not
        // render at all, because comparing columns is a desk task and a horizontal scroller
        // hides the very column that is empty for everybody. So `overflow-x` and the 36rem
        // floor both move behind `sm:`: leaving either on the phone would give the row's
        // chips a 36rem box to sit in and reintroduce the scroller by the back door.
        <div className="sm:overflow-x-auto -mx-1 px-1">
          <div className="sm:min-w-[36rem]">
            {/* RULE 4 (redline). The header row and every client row share ONE template —
                `132px repeat(n,44px) minmax(0,1fr)` — with the mark cells FIXED at 44px and
                centred, so the columns stay aligned as names change length; only the name
                cell and `რა ვქნათ` flex, and both truncate.
                ⚠️ This row has now been wrong twice. First it truncated each name to two
                characters (`მა მე ფა დე`) — not words. Then I set it vertically, which
                was my own invention for a problem the design had already solved: it read
                badly, reserved 7rem above the grid, and threw away the leverage number.
                The answer is a distinct SHORT WORD plus the count.
                ⚠️ AND IT IS DESK-ONLY. `hidden sm:grid` — the phone has no columns to head,
                so a header naming them would be a promise the rows below do not keep. */}
            <div className="hidden sm:grid gap-x-1 items-end pb-1 border-b border-border"
              style={{ gridTemplateColumns: `132px repeat(${cols.length}, 44px) minmax(0,1fr)` }}>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate self-end">{t('mv2_col_client')}</span>
              {cols.map(k => (
                <span key={k}
                  title={k === OVERFLOW_COL
                    ? [...hiddenKeys].map(hk => conditionLabel(hk, ans)).join(' · ')
                    : conditionLabel(k, ans)}
                  className={`text-[10px] cursor-help truncate text-center ${ownerAnswerKind(k) ? 'text-amber-500/90' : 'text-muted-foreground'}`}>
                  {columnLabel(k, ans)}
                </span>
              ))}
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate self-end">{t('mv2_col_what_to_do')}</span>
            </div>

            {GROUPS.map(g => {
              const list = byGroup.get(g.key) || []
              // Every group but READY simply disappears when empty. READY keeps its heading
              // — measured, READY is genuinely 0 on most properties, and a panel that just
              // omits it reads as "no such thing" instead of "nothing here yet, and here is
              // what would fill it" (05-v2-answers).
              if (!list.length && g.key !== 'READY') return null
              return (
                <div key={g.key}>
                  <p className={`text-xs font-bold pt-2 ${list.length ? g.tone : 'text-muted-foreground/60'}`}>
                    <span className={`inline-block w-2 h-2 rounded-[3px] mr-1.5 align-middle ${list.length ? g.dot : 'bg-muted'}`} />
                    {t(g.labelKey)} <span className="font-normal text-muted-foreground">· {list.length}</span>
                    {/* RULE 5: the clause is what turns a coloured dot and a number into
                        something an agent can act on. Kept at zero, deliberately. */}
                    <span className="font-normal text-muted-foreground/70"> — {t(g.meansKey)}</span>
                  </p>
                  {!list.length && (
                    <p className="text-xs text-muted-foreground/70 pl-3.5 py-1">{t('mv2_group_ready_empty')}</p>
                  )}
                  {list.map(m => {
                    // RULE 6: no name, no phone, no representative -> nothing an agent can
                    // dial, so the row does not belong in a call queue. Dropped rather than
                    // rendered as the generic noun four times over.
                    const who = clientLabel(m)
                    if (!who) return null
                    const checks = m.checks || []
                    const byKey = new Map(checks.map(c => [c.key, c]))
                    const isOpen = open === m.requirement_id
                    const b = bandsOf(checks, m.blockers)
                    // How many of THIS client's open questions the owner still owes — the
                    // number the action sentence quotes.
                    const owedByOwner = checks.filter(c => c.status === 'unknown'
                      && whoAnswers(c) === 'owner' && !isDeclined(c.key, ans)
                      && (m.blockers || []).includes(c.key)).length
                    const action = actionText(m, ans, hoisted, owedByOwner) || null
                    // ── What the phone's chip line says about THIS row (round-2 §1) ──────
                    // Read off the same `checks` the mark cells read, not off the columns:
                    // the columns are a capped selection made across all rows, and a miss
                    // that lost its column would silently vanish from the phone. Chips are
                    // per-ROW, so they can afford to be complete.
                    const matched = checks.filter(c => c && c.status === 'pass').length
                    const misses = checks.filter(c => c && cellOf(c).mark === '✗')
                    // ONE chip, never one per question — the strip above already asks them.
                    const askedAbove = checks.filter(c => c && hoisted.has(c.key)).length
                    return (
                      <div key={m.requirement_id} className="border-b border-border/40 last:border-0">
                        <button type="button"
                          onClick={() => setOpen(o => o === m.requirement_id ? null : m.requirement_id)}
                          // ⚠️ THE TEMPLATE MOVES BEHIND `sm:` VIA A CUSTOM PROPERTY, and it
                          // has to: an inline `gridTemplateColumns` beats any class, so the
                          // phone could never opt out of it. The column count is dynamic, so
                          // the VALUE stays inline (Tailwind cannot generate a class it never
                          // sees) and only the static `sm:grid-cols-[var(--mv2-row)]` reads
                          // it. At ≥640px that resolves to the exact same track list this row
                          // has always had; below it the grid falls back to one column and
                          // the three children stack — name, action sentence, chips.
                          className="w-full grid gap-x-2 items-center py-1.5 text-left hover:bg-accent/30 sm:grid-cols-[var(--mv2-row)]"
                          style={{ '--mv2-row': `132px repeat(${cols.length}, 44px) minmax(0,1fr)` }}>
                          <span className="flex items-center gap-1 min-w-0">
                            {/* One glyph, rotated — Icon.jsx carries chevron-down and no
                                chevron-right, and adding a second path for a 90° turn is
                                how icon sets grow duplicates. */}
                            <Icon name="chevron-down" size={11}
                              className={`shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                            {/* The role, on the COLLAPSED row (owner-asked 2026-08-08). Every
                                row here is a client, so this marks the ENTITY rather than
                                telling rows apart — the job the old OBJECT_ICON 🙋 did before
                                the icon migration.
                                MEASURED rather than guessed, because the fear was that an
                                11px glyph would push names off a 132px cell: with the gap
                                dropped 1.5→1 to pay for it, five of six fixture names lose
                                ZERO width and still fit. The sixth (`თედო კვარაცხელია`)
                                clips — and clips identically with the icon hidden, so it was
                                already too long and this is not what broke it.
                                ⚠️ If more names start clipping, widen the first column;
                                do not shrink the glyph. 11px is the floor at which these
                                filled Material shapes stay readable. */}
                            {/* ⚠️ The icon follows what the LABEL actually names, not what the
                                column is headed. A represented row shows another agency's
                                agent ("Luka Bigvava-ის კლიენტი"), so it takes the AGENT
                                glyph; a client row takes the client one. Pinning it to
                                ROLE_ICON.client because the column says `კლიენტი` is exactly
                                how the bare-name defect above got louder instead of caught. */}
                            <Icon name={who.via ? ROLE_ICON.agent : ROLE_ICON.client} size={11}
                              title={who.via ? t('mv2_role_agent') : t('mv2_role_client')}
                              className="shrink-0 opacity-70" />
                            <span className={`text-xs text-foreground truncate ${who.mono ? 'font-mono tabular-nums' : ''}`}>{who.text}</span>
                          </span>
                          {/* The MARK CELLS are the grid, and the grid is desk-only (round-2
                              §1). One `hidden sm:contents` wrapper rather than a class on
                              each cell — the same idiom matchBands' ROW_GRID uses: at ≥640px
                              `display:contents` dissolves it and the identical N children
                              land in the identical N tracks, so the desktop DOM this produces
                              is unchanged. */}
                          <span className="hidden sm:contents">
                          {cols.map(k => {
                            if (!detailReady) return <span key={k} className="text-center"><LoadingCell /></span>
                            // RULE 4: the collapsed remainder is a COUNT of what is met in
                            // there, never a stub of one arbitrary condition's name.
                            if (k === OVERFLOW_COL) {
                              const met = [...hiddenKeys].filter(hk => byKey.get(hk)?.status === 'pass').length
                              return (
                                <span key={k} className="text-xs text-center text-muted-foreground tabular-nums"
                                  title={[...hiddenKeys].map(hk => conditionLabel(hk, ans)).join(' · ')}>
                                  {met ? `${met} ✓` : '–'}
                                </span>
                              )
                            }
                            const cell = cellOf(byKey.get(k))
                            return (
                              <span key={k} className={`text-xs text-center ${cell.cls}`}
                                title={`${conditionLabel(k, ans)}${cell.titleKey ? ` — ${t(cell.titleKey)}` : ''}`}>
                                {cell.mark}
                              </span>
                            )
                          })}
                          </span>
                          {/* The action sentence is the last GRID cell on the desk and the
                              second LINE on the phone — same element, same text, no second
                              copy to drift. It only looks different because there is one
                              column below 640px. */}
                          <span className="text-xs text-muted-foreground truncate" title={action || undefined}>
                            {detailReady ? (action || '') : t('mv2_details_loading')}
                          </span>
                          {/* ── The phone's chip line (round-2 §1) ────────────────────────
                              The columns become chips on the row the columns described, in
                              the client card's own chip vocabulary — `msug_m_matched` and
                              its emerald pill are reused verbatim, because two surfaces
                              spelling "3 matched" two ways is the drift B273 names.
                              The hoisted questions collapse to ONE chip: the strip at the
                              top of the panel already asks them, and a chip per question
                              would repeat the whole strip on every row.
                              While the detail is out this carries the SAME LoadingCell the
                              mark cells use — a second loading shape would say the two
                              halves of one row are waiting on different things. */}
                          <span className="flex flex-wrap gap-1 pt-0.5 sm:hidden">
                            {!detailReady ? <LoadingCell /> : (<>
                              {matched > 0 && (
                                <span className={pill('none', { extra: 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400' })}>
                                  ✓ {tLog('msug_m_matched', { n: matched })}
                                </span>
                              )}
                              {/* ⚠️ ROUND 3 §5 — ONE MISS TONE, RED. The chip used to take
                                  `cellOf(c).cls`, which carries a muted tone for a BINDING
                                  miss — a tone that can never appear here, because a hard
                                  fail classifies the row REJECT and a REJECT row never
                                  reaches this panel. *Unreachable spec is worse than absent
                                  spec: someone eventually makes it reachable to justify it,
                                  and that is how a REJECT row ends up in a call queue.* Every
                                  miss on this surface is one the agent can act on, so they
                                  all read the same. (A client blocked only by an immovable
                                  condition is worth seeing — but on the property side's
                                  ჰკითხე კლიენტს group, as a conversation with a named
                                  person, never as a muted chip smuggled into the queue.) */}
                              {misses.map(c => (
                                <span key={c.key} className={pill('none', { extra: 'border-border text-red-400' })}>
                                  {conditionLabel(c.key, ans)}
                                </span>
                              ))}
                              {askedAbove > 0 && (
                                <span className={pill('none', { extra: 'border-amber-500/40 text-amber-600 dark:text-amber-400' })}>
                                  {tLog('mv2_chip_asked_above', { n: askedAbove })}
                                </span>
                              )}
                            </>)}
                          </span>
                        </button>

                        {isOpen && (
                          <div className="pb-2 pl-3.5 pr-1">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                              <span className="flex items-center gap-1.5 min-w-0">
                                <ClassBadge classification={m.classification} />
                                <RegisteredAgo iso={m.created_at} activeIso={m.last_activity_at}
                                  titleKey="desk_match_registered_client_title" />
                              </span>
                              {/* 05-v2-answers Q4: the row actions appear ON EXPAND, at full
                                  32×32 — never on hover (fails on touch) and never on the
                                  collapsed row (five icons would double its height). The
                                  expand IS the "I'm working this one" gesture. Same five
                                  actions, same seeds and same modals as the v1 card — the
                                  rows are requirements and the anchor is the property, so
                                  every id a seed needs is already here. */}
                              <span className="flex items-center gap-2 shrink-0">
                                <VerdictActions inline state={effVerdict(m)} busy={verdictBusy}
                                  onSet={s => applyVerdict(m, s)} />
                                <RowActions items={[
                                  { key: 'open', icon: 'user-check', title: t('mv2_role_client'),
                                    onClick: () => onOpenDemand?.(m.requirement_id) },
                                  { key: 'pin', icon: 'pin', title: t('desk_match_act_shortlist'),
                                    busy: verdictBusy, active: effVerdict(m) === 'shortlist',
                                    onClick: () => applyVerdict(m, effVerdict(m) === 'shortlist' ? 'clear' : 'shortlist') },
                                  // Sends the ANCHOR property to THIS client — the recipient
                                  // is known here, so "already sent" can name who.
                                  { key: 'share', icon: 'link', title: t('share_tooltip'),
                                    onClick: () => setShareSeed({
                                      propertyId, propertyLabel: address || null,
                                      recipientPartyId: m.person?.party_id || null,
                                      recipientName: m.person?.name || null,
                                    }) },
                                  { key: 'meeting', icon: 'calendar', title: t('meeting_create_btn'),
                                    onClick: () => setMeetingSeed(seedFromMatchRow('property', m,
                                      { propertyId, propertyLabel: address || null })) },
                                  // The price thread. person is null when the client is
                                  // masked — the requirement id still resolves the party
                                  // server-side, so the thread opens either way.
                                  { key: 'nego', icon: 'banknote', title: t('nego_tooltip'),
                                    onClick: () => setNegoSeed({
                                      propertyId, clientPartyId: m.person?.party_id || null,
                                      requirementId: m.requirement_id, intent: m.intent || null,
                                      propertyLabel: address || null,
                                      clientLabel: m.person?.name || null,
                                    }) },
                                  { key: 'reject', icon: 'x', busy: verdictBusy,
                                    title: t(effVerdict(m) === 'rejected' ? 'desk_match_act_undo' : 'desk_match_act_reject'),
                                    onClick: () => applyVerdict(m, effVerdict(m) === 'rejected' ? 'clear' : 'rejected') },
                                ]} />
                              </span>
                            </div>

                            {/* The evidence bands carry the client's ask, the property's
                                value and the provenance — the three things the agent used to
                                have to open two other cards to learn. */}
                            <Band labelKey="mv2_band_why" count={b.why.length}
                              headers={['mv2_col_client_asks', 'mv2_col_on_property', 'mv2_col_source']}
                              tint="bg-emerald-500/[.06] text-emerald-600 dark:text-emerald-400">
                              {b.why.map(c => <BandRow key={c.key} check={c} ans={ans} m={m}
                                lever={<span className="text-xs text-muted-foreground/70 whitespace-nowrap">{sourceOf(c, ans)}</span>} />)}
                            </Band>
                            <Band labelKey="mv2_band_mismatch" count={b.mismatch.length}
                              headers={['mv2_col_client_asks', 'mv2_col_on_property', null]}
                              tint="bg-red-500/[.06] text-red-500 dark:text-red-400">
                              {/* 02-spec: "✗ row + lever button". The lever opens the CLIENT's
                                  demand, because the only thing that moves a mismatch is the
                                  client relaxing the condition — and that is an edit to their
                                  requirement, not a note on this row. It is offered only where
                                  a lever exists: a HARD fail is a binding condition and the
                                  design says explicitly not to offer to remove it. */}
                              {b.mismatch.map(c => <BandRow key={c.key} check={c} ans={ans} m={m}
                                lever={c.hard ? null : (
                                  <button type="button" onClick={() => onOpenDemand?.(m.requirement_id)}
                                    className={pill('add', { door: true, sm: true, extra: 'h-[22px] text-xs' })}>
                                    {t('mv2_will_concede')}
                                  </button>
                                )} />)}
                            </Band>
                            <Band labelKey="mv2_band_to_fill"
                              count={b.ownerBlocking.length + b.clientBlocking.length}
                              show={b.ownerBlocking.length + b.clientBlocking.length + b.quiet.length}
                              headers={['mv2_col_who_answers', 'mv2_col_known_today', null]}
                              tint="bg-amber-500/[.06] text-amber-600 dark:text-amber-400">
                              {b.ownerBlocking.map(c => {
                                // B586: the band still LISTS a declined subject — it is
                                // genuinely still open, and hiding it here would contradict
                                // the check's own `unknown`. What changes is what it says:
                                // "asked · wouldn't say", with provenance, instead of
                                // "nobody has asked yet". The strip is where it stops
                                // demanding a call; the band is where the record lives.
                                const dec = ans.declinedRows?.get(declineSubjectOf(c.key))
                                return (
                                  <GapRow key={c.key} check={c} ans={ans}
                                    tone={dec ? 'text-muted-foreground' : 'text-amber-500'}
                                    known={dec
                                      ? `${t('mv2_wouldnt_say')} · ${provenanceOf({ ...dec, last_seen_at: dec.observed_at })}`
                                      : t('mv2_not_asked_yet')}
                                    leverage={dec ? 0 : (colLeverage.get(c.key) || 0)}
                                    control={<OwnerFill checkKey={c.key} busy={fillBusy} canRecord={canRecord}
                                      listingId={listingId} propertyId={propertyId} agentId={agentId}
                                      onRun={runFill} ans={ans} />} />
                                )
                              })}
                              {/* The client's side — violet, and answerable HERE since
                                  2026-08-08. `known` is what we already have from the demand,
                                  so an agent can see they are confirming rather than guessing. */}
                              {b.clientBlocking.map(c => {
                                const fill = clientFillFor(c, { predicate: ans.rulesById?.get(ruleIdOf(c.key))?.predicate || null })
                                return (
                                  <GapRow key={c.key} check={c} ans={ans} tone="text-violet-500"
                                    known={knownFromDemand(c, m)} leverage={colLeverage.get(c.key) || 0}
                                    control={fill ? <ClientFill check={c} fill={fill} m={m} agentId={agentId}
                                      busy={fillBusy} onRun={runFill} onOpenDemand={onOpenDemand} /> : null} />
                                )
                              })}
                              {/* The quiet tier: nobody asked and nothing hangs on it. Same
                                  band because an agent on the phone fills both in one breath;
                                  visibly lighter, and never counted in the numbers above. */}
                              {!!b.quiet.length && (<>
                                <p className="text-[10px] uppercase tracking-wide px-2.5 pt-1 text-muted-foreground/70">
                                  {t('mv2_blocks_nothing')} <span className="opacity-70">· {b.quiet.length}</span>
                                </p>
                                <div className="opacity-60">
                                  {b.quiet.map(c => (
                                    <QuietRow key={c.key} check={c} ans={ans}
                                      control={whoAnswers(c) === 'owner'
                                        ? <OwnerFill checkKey={c.key} busy={fillBusy} canRecord={canRecord}
                                            listingId={listingId} propertyId={propertyId} agentId={agentId}
                                            onRun={runFill} ans={ans} />
                                        : null} />
                                  ))}
                                </div>
                              </>)}
                            </Band>

                            {(() => {
                              const s = summarySentence(b)
                              return s ? <p className="text-xs text-muted-foreground mt-1.5">{s}</p> : null
                            })()}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* RULE 8: one line at the foot naming every mark on the table. Before this there was
          no legend at all, and a mark an agent cannot decode is worse than an absent column —
          they will guess, and a wrong guess about `–` versus `✗` invents a refusal. */}
      {!!rows.length && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 pt-1.5 border-t border-border/50 text-[10px] text-muted-foreground/80">
          <span><span className="text-emerald-500">✓</span> {t('mv2_legend_met')}</span>
          <span><span className="text-red-400">✗</span> {t('mv2_legend_missed')}</span>
          <span><span className="text-violet-500">✗</span> {t('mv2_legend_client_cond')}</span>
          <span><span className="text-amber-500">?</span> {t('mv2_legend_owner_unasked')}</span>
          <span><span className="text-muted-foreground/50">–</span> {t('mv2_legend_never_asked')}</span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-[22px] h-[8px] rounded-[2px] bg-muted align-middle" />
            {t('mv2_legend_loading')}
          </span>
        </div>
      )}

      {/* Two honest footnotes rather than a silently narrowed view. Both say what was left
          out and where it went — "no silent caps": a truncated surface that says nothing
          reads as a complete one. */}
      {!!hidden && (
        <p className="text-xs text-muted-foreground/70 mt-1.5">
          {tLog('mv2_more_questions', { n: hidden })} · {t('mv2_expand')}
        </p>
      )}
      {capped && (
        <p className="text-xs text-muted-foreground/70 mt-1 tabular-nums">
          {rows.length} / {eligible}
        </p>
      )}

      {/* No refresh prop reaches this panel, so a booking made here announces itself with a
          window event; IntakeTab listens and refreshes the feed + 📅 chips (review 2026-09-23). */}
      {meetingSeed && <MeetingModal seed={meetingSeed} onClose={() => setMeetingSeed(null)}
        onCreated={m => window.dispatchEvent(new CustomEvent('sntv:meeting-created', { detail: { meeting: m || null } }))} />}
      {shareSeed && <ShareModal {...shareSeed} onClose={() => setShareSeed(null)} />}
      {negoSeed && <NegotiationModal {...negoSeed} onClose={() => setNegoSeed(null)} />}
    </div>
  )
}

export default MatchMatrix
